import { useState, useEffect } from "react";
import api from "../lib/api";
import PodLayout from "../components/PodLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { Video, Calendar, Plus, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "Leadership and School Growth", "Career Growth and Professional Development",
  "Online Tutoring and Digital Opportunities", "Money, Sustainability and Income Growth",
  "Technology and Innovation",
];

export default function PodWebinarsPage() {
  const { user } = useAuth();
  const isStaff = ["super_admin", "admin", "instructor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "", subcategory: "", starts_at: "", duration_minutes: 60, host: "", join_url: "", recording_url: "", status: "scheduled" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/pod/webinars").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const now = Date.now();
  const visible = items.filter(w => {
    if (filter && w.category !== filter) return false;
    const start = w.starts_at ? Date.parse(w.starts_at) : 0;
    if (tab === "upcoming") return w.status !== "past" && (!start || start >= now - 60 * 60 * 1000);
    if (tab === "past") return w.status === "past" || (start && start < now - 60 * 60 * 1000);
    if (tab === "recorded") return !!w.recording_url || w.status === "recorded";
    return true;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/pod/webinars", { ...form, duration_minutes: parseInt(form.duration_minutes, 10) || 60 });
      toast.success("Webinar created");
      setOpen(false); setForm({ title: "", description: "", category: "", subcategory: "", starts_at: "", duration_minutes: 60, host: "", join_url: "", recording_url: "", status: "scheduled" });
      load();
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const attend = async (id, url) => {
    try { await api.post(`/pod/webinars/${id}/attend`, {}); } catch (e) { /* ignore */ }
    if (url) window.open(url, "_blank");
  };

  return (
    <PodLayout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="pod-webinars-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">POD</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Webinar Centre</h1>
          </div>
          {isStaff && <Button onClick={() => setOpen(true)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="new-webinar-btn"><Plus size={14} weight="bold" /> New Webinar</Button>}
        </div>

        <div className="flex gap-2 mb-4 border-b border-slate-200">
          {[{ k: "upcoming", l: "Upcoming" }, { k: "past", l: "Past" }, { k: "recorded", l: "Recorded" }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`webinar-tab-${t.k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-blue-800 text-blue-900" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{t.l}</button>
          ))}
        </div>

        <div className="mb-4">
          <Select value={filter} onValueChange={v => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none max-w-xs"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-slate-500">No webinars in this section yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map(w => {
              const start = w.starts_at ? new Date(w.starts_at) : null;
              const canJoin = (w.status === "scheduled" || w.status === "live") && w.join_url;
              return (
                <Card key={w.id} className="rounded-none border border-slate-200 hover:shadow-md transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider ${w.status === "live" ? "bg-red-100 text-red-700" : w.status === "past" ? "bg-slate-200 text-slate-700" : "bg-blue-100 text-blue-800"}`}>{w.status}</span>
                      {w.recording_url && <span className="inline-block px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-amber-100 text-amber-800">Recorded</span>}
                    </div>
                    <h3 className="font-bold text-slate-950 text-base leading-snug mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{w.title}</h3>
                    <p className="text-xs text-slate-500 mb-2">{w.category}</p>
                    {start && <p className="text-xs text-slate-600 mb-2 flex items-center gap-1"><Calendar size={12} /> {start.toLocaleString()}</p>}
                    {w.host && <p className="text-xs text-slate-600 mb-3">Host: <b>{w.host}</b></p>}
                    {w.description && <p className="text-xs text-slate-600 mb-3 line-clamp-3">{w.description}</p>}
                    <div className="flex gap-2">
                      {canJoin && <Button size="sm" onClick={() => attend(w.id, w.join_url)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1 text-xs" data-testid={`join-webinar-${w.id}`}><Video size={12} weight="fill" /> Join</Button>}
                      {w.recording_url && <Button size="sm" variant="outline" onClick={() => attend(w.id, w.recording_url)} className="rounded-none gap-1 text-xs" data-testid={`watch-recording-${w.id}`}><Play size={12} weight="fill" /> Recording</Button>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none max-w-xl">
            <DialogHeader><DialogTitle>New Webinar</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="webinar-title-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subcategory</Label><Input value={form.subcategory} onChange={e => setForm({ ...form, subcategory: e.target.value })} className="rounded-none" /></div>
              <div><Label>Starts At</Label><Input type="datetime-local" value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} className="rounded-none" /></div>
              <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} className="rounded-none" /></div>
              <div><Label>Host</Label><Input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} className="rounded-none" /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["scheduled", "live", "past", "recorded"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Join URL</Label><Input value={form.join_url} onChange={e => setForm({ ...form, join_url: e.target.value })} className="rounded-none" /></div>
              <div className="col-span-2"><Label>Recording URL</Label><Input value={form.recording_url} onChange={e => setForm({ ...form, recording_url: e.target.value })} className="rounded-none" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none" /></div>
            </div>
            <Button disabled={saving} onClick={save} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full" data-testid="save-webinar-btn">{saving ? "Saving…" : "Create Webinar"}</Button>
          </DialogContent>
        </Dialog>
      </div>
    </PodLayout>
  );
}
